import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from './modules/auth/stores/auth-store'
import NotFoundPage from './modules/core/pages/not-found.vue'
import AdminRoutes from './modules/admin/routes/admin-routes'
import AttachingRoutes from './modules/attaching/routes/attaching-routes'
import AuthRoutes from './modules/auth/routes/auth-routes'
import ConnectingRoutes from './modules/connecting/routes/connecting-routes'
import BillingRoutes from './modules/billing/routes/billing-routes'
import InvestingRoutes from './modules/investing/routes/investing-routes'
import DashboardRoutes from './modules/dashboard/routes/dashboard-routes'
import InsightsRoutes from './modules/insights/routes/insights-routes'
import PublicRoutes from './modules/public/routes/public-routes'
import SandboxRoutes from './modules/sandbox/routes/sandbox-routes'
import SigningRoutes from './modules/signing/routes/signing-routes'
import TaxRoutes from './modules/tax/routes/tax-routes'
import FilingRoutes from './modules/filing/routes/filing-routes'
import MessagingRoutes from './modules/messaging/routes/messaging-routes'
import WorkspaceRoutes from './modules/workspace/routes/workspace-routes'

const routes = [
  ...AdminRoutes,
  ...AttachingRoutes,
  ...AuthRoutes,
  ...BillingRoutes,
  ...ConnectingRoutes,
  ...InvestingRoutes,
  ...PublicRoutes,
  ...SandboxRoutes,
  ...SigningRoutes,
  ...TaxRoutes,
  ...DashboardRoutes,
  ...InsightsRoutes,
  ...FilingRoutes,
  ...MessagingRoutes,
  ...WorkspaceRoutes,

  // Generic 404 — Rails redirect controller sends users here when their
  // requested subdomain doesn't match a real Group. The auth guard's
  // allowlist (below) skips authentication checks for this route so it
  // never triggers the refresh/logout cycle.
  { path: '/not-found', name: 'not-found', component: NotFoundPage },

  // Any URL that doesn't match a registered route lands on the NotFound
  // page — covers typo'd paths (e.g. /:slug/dashboard2) and fully random
  // URLs alike.
  { path: '/:pathMatch(.*)*', redirect: { name: 'not-found' } },
]

const router = createRouter({
  history: createWebHistory(),
  // @ts-ignore
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      }
    }
    return {
      top: 0,
    }
  },
})

// handle auth flow where user arrives from the rails app with a temp token as a GET param
router.beforeEach(async (to, _from, next) => {
  if (to.query.t === undefined) next()

  const authStore = useAuthStore()

  await authStore.refresh(typeof to.query.t === 'string' ? to.query.t : to.query.t.join(''), to)
  const query = { ...to.query }
  delete query.t
  next({ path: to.path, query: query })
})

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  if (
    [
      'login',
      'logout',
      'auth.verify',
      'auth.enable-two-factor',
      'request-password-reset',
      'request-email-verification',
      'email-verification-instructions',
      'verify-email',
      'public.welcome',
      'public.goodbye',
      'public.groups.apply',
      'reset-password',
      'reset-two-factor',
      'sandbox.sandbox',
      'spv-overview-v2',
      'not-found',
    ].includes(to.name as string)
  ) {
    return next()
  }

  if (!authStore.is_authenticated) {
    await authStore.logout()
    return next({ name: 'login', query: { redirect: to.fullPath } })
  }

  const result = await authStore.refresh(authStore.access_token, to)

  if (!result.ok) {
    // Bad slug in the URL → send the user to the 404 page without ending
    // their session. Genuine auth failure (token expired, etc.) still ends
    // the session and bounces to login as before.
    if (result.reason === 'slug_not_found') {
      return next({ name: 'not-found' })
    }
    await authStore.logout()
    return next({ name: 'login', query: { redirect: to.fullPath } })
  }

  if (to.name === 'dashboard' && authStore.isInvestor) {
    if (to.params.slug === 'connections') {
      return next({
        name: 'connecting.individuals',
        params: { slug: to.params.slug, individualId: authStore.current_user.investor_id },
      })
    }
    return next({
      name: 'investing.individual-overview',
      params: { slug: to.params.slug, individual_id: authStore.current_user.investor_id },
    })
  }

  const connectionsInvestorRoutes = [
    'connecting.individuals',
    'connecting.onboarding',
    'connecting.individuals.edit',
    'connecting.agreement',
  ]
  if (
    to.params.slug === 'connections' &&
    !authStore.is_site_or_group_admin &&
    authStore.current_user?.investor_id &&
    !(to.name as string)?.startsWith('public.') &&
    !connectionsInvestorRoutes.includes(to.name as string)
  ) {
    return next({
      name: 'connecting.individuals',
      params: { slug: 'connections', individualId: authStore.current_user.investor_id },
    })
  }

  next()
})

router.beforeEach((to, _from, next) => {
  // TODO fix typescript error
  // @ts-ignore
  document.title = to.meta?.title || 'Venture360'
  next()
})

router.afterEach((_to, _from) => {
  if (window.Intercom) {
    window.Intercom('update', {})
  }
})

export default router
